// src/services/userSearchService.ts
import { logger } from '../utils/logger';
// FIXED: Added is_admin to SearchResult type

import { supabase } from './supabase';

export interface SearchResult {
  id: string;
  username: string;
  display_name?: string;
  avatar_url: string | null;
  avatar?: any;
  is_admin?: boolean;
  connection_status: 'none' | 'pending_sent' | 'pending_received' | 'connected' | 'blocked';
  match_id?: string;
}

export async function searchUserByUsername(
  username: string
): Promise<{ success: boolean; user?: SearchResult; error?: string }> {
  try {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) {
      return { success: false, error: 'Not authenticated' };
    }

    // Search for the user - INCLUDE is_admin
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id, username, display_name, avatar_url, meeple_avatar, is_admin')
      .ilike('username', username)
      .single();

    if (profileError || !profile) {
      return { success: false, error: 'User not found' };
    }

    // Don't allow searching for yourself
    if (profile.id === currentUser.id) {
      return { success: false, error: 'Cannot search for yourself' };
    }

    // Check if blocked
    const { data: blockedData } = await supabase
      .from('blocked_users')
      .select('*')
      .or(`and(blocker_id.eq.${currentUser.id},blocked_id.eq.${profile.id}),and(blocker_id.eq.${profile.id},blocked_id.eq.${currentUser.id})`)
      .maybeSingle();

    if (blockedData) {
      return { success: false, error: 'User not found' };
    }

    // Check connection status
    const { data: matchData } = await supabase
      .from('matches')
      .select('*')
      .or(`and(user1_id.eq.${currentUser.id},user2_id.eq.${profile.id}),and(user1_id.eq.${profile.id},user2_id.eq.${currentUser.id})`)
      .maybeSingle();

    let connectionStatus: SearchResult['connection_status'] = 'none';
    let matchId: string | undefined;

    if (matchData) {
      matchId = matchData.id;
      if (matchData.status === 'accepted') {
        connectionStatus = 'connected';
      } else if (matchData.status === 'pending') {
        connectionStatus = matchData.user1_id === currentUser.id ? 'pending_sent' : 'pending_received';
      }
    }

    const searchResult: SearchResult = {
      id: profile.id,
      username: profile.username,
      display_name: profile.display_name,
      avatar_url: profile.avatar_url,
      avatar: profile.meeple_avatar,
      is_admin: profile.is_admin,
      connection_status: connectionStatus,
      match_id: matchId,
    };

    return { success: true, user: searchResult };
  } catch (error) {
    logger.error('Error searching for user:', error);
    return { success: false, error: 'An error occurred while searching' };
  }
}